import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Avatar,
  Divider,
  Grid,
  Chip,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Close,
  Event,
  AccessTime,
  AttachMoney,
  Person,
  Phone,
  Email,
  EventNote,
  CheckCircle,
  Cancel,
  LocationOn,
} from '@mui/icons-material';
import AppointmentStatusBadge from './AppointmentStatusBadge';
import { appointmentService } from '../services/appointmentService';
import {
  getServiceAddressDisplay,
  shouldShowServiceAddressSection,
} from '../utils/serviceAddressDisplay';

interface AppointmentDetailsModalProps {
  open: boolean;
  onClose: () => void;
  appointment: any;
  userRole?: 'PATIENT' | 'CAREGIVER' | 'ADMIN' | null;
  onAction: (accepted?: boolean) => void;
}

const AppointmentDetailsModal: React.FC<AppointmentDetailsModalProps> = ({
  open,
  onClose,
  appointment,
  userRole,
  onAction,
}) => {
  const [cancelling, setCancelling] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const formatPrice = (price?: number | string | null) => {
    if (!price) return 'Not set';
    const numericPrice = typeof price === 'string' ? parseFloat(price) : price;
    if (isNaN(numericPrice)) return 'Not set';
    return `$${numericPrice.toFixed(2)}`;
  };

  const handleCancelAppointment = async () => {
    try {
      setCancelling(true);
      setError(null);
      await appointmentService.cancelAppointment(appointment.id);
      onAction();
      setShowCancelConfirm(false);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to cancel appointment');
    } finally {
      setCancelling(false);
    }
  };

  const handleAcceptAppointment = async () => {
    if (!window.confirm('Are you sure you want to accept this appointment request?')) {
      return;
    }

    try {
      setAccepting(true);
      setError(null);
      await appointmentService.updateAppointment(appointment.id, { status: 'CONFIRMED' });
      onAction(true); // Pass true to indicate acceptance
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to accept appointment');
    } finally {
      setAccepting(false);
    }
  };

  const handleRejectAppointment = async () => {
    if (!window.confirm('Are you sure you want to reject this appointment request?')) {
      return;
    }

    try {
      setRejecting(true);
      setError(null);
      await appointmentService.updateAppointment(appointment.id, { status: 'CANCELLED' });
      onAction(false); // Pass false to indicate rejection
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reject appointment');
    } finally {
      setRejecting(false);
    }
  };

  const isPatient = userRole === 'PATIENT';
  const isCaregiver = userRole === 'CAREGIVER';
  const isPending = appointment.status === 'REQUESTED';
  const otherParty = isPatient ? appointment.caretaker : appointment.patient;
  const canCancel = ['SCHEDULED', 'CONFIRMED'].includes(appointment.status);
  const serviceAddressDisplay = getServiceAddressDisplay(
    appointment.serviceAddress,
    appointment.status,
    userRole,
  );
  const showServiceAddress = shouldShowServiceAddressSection(
    appointment.serviceAddress,
    appointment.status,
    userRole,
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Appointment Details</Typography>
          <AppointmentStatusBadge status={appointment.status} size="medium" />
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {showCancelConfirm ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2" gutterBottom>
              Are you sure you want to cancel this appointment?
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {isPatient 
                ? 'Cancelling within 24 hours may incur a cancellation fee.'
                : 'The patient will be notified of the cancellation.'}
            </Typography>
            <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
              <Button
                size="small"
                variant="contained"
                color="error"
                onClick={handleCancelAppointment}
                disabled={cancelling}
                startIcon={cancelling && <CircularProgress size={16} />}
              >
                {cancelling ? 'Cancelling...' : 'Yes, Cancel Appointment'}
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={() => setShowCancelConfirm(false)}
                disabled={cancelling}
              >
                No, Keep It
              </Button>
            </Box>
          </Alert>
        ) : (
          <>
            {/* Other Party Information */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                {isPatient ? 'Caregiver Information' : 'Careseeker Information'}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Avatar
                  src={otherParty?.avatar}
                  sx={{ width: 64, height: 64 }}
                >
                  {otherParty?.firstName?.[0]}{otherParty?.lastName?.[0]}
                </Avatar>
                <Box>
                  <Typography variant="h6">
                    {otherParty?.firstName} {otherParty?.lastName}
                  </Typography>
                  {otherParty?.email && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                      <Email fontSize="small" color="action" />
                      <Typography variant="body2" color="text.secondary">
                        {otherParty.email}
                      </Typography>
                    </Box>
                  )}
                  {otherParty?.phoneNumber && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                      <Phone fontSize="small" color="action" />
                      <Typography variant="body2" color="text.secondary">
                        {otherParty.phoneNumber}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Appointment Details */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Appointment Details
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary">
                      Service
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {appointment.service?.name || 'N/A'}
                    </Typography>
                    {appointment.service?.description && (
                      <Typography variant="body2" color="text.secondary">
                        {appointment.service.description}
                      </Typography>
                    )}
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary">
                      Duration
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {appointment.duration} minutes
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary">
                      Date
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {formatDate(appointment.scheduledAt)}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary">
                      Time
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {formatTime(appointment.scheduledAt)}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>

            {showServiceAddress && serviceAddressDisplay && (
              <>
                <Divider sx={{ my: 3 }} />
                <Box
                  data-testid="service-address-section"
                  sx={{ mb: 3 }}
                >
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Service Address
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    <LocationOn fontSize="small" color="action" sx={{ mt: 0.25 }} />
                    <Typography
                      variant="body1"
                      fontWeight={serviceAddressDisplay.isRedactedPlaceholder ? 'regular' : 'medium'}
                      color={
                        serviceAddressDisplay.isRedactedPlaceholder
                          ? 'text.secondary'
                          : 'text.primary'
                      }
                      data-testid={
                        serviceAddressDisplay.isRedactedPlaceholder
                          ? 'service-address-redacted'
                          : 'service-address-value'
                      }
                    >
                      {serviceAddressDisplay.text}
                    </Typography>
                  </Box>
                </Box>
              </>
            )}

            {/* Payment Information */}
            {appointment.totalAmount != null && (
              <>
                <Divider sx={{ my: 3 }} />
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Payment Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Total Amount
                        </Typography>
                        <Typography variant="h6" color="primary">
                          {formatPrice(appointment.totalAmount)}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Payment Status
                        </Typography>
                        <Box sx={{ mt: 0.5 }}>
                          <Chip
                            label={appointment.paymentStatus}
                            size="small"
                            color={appointment.paymentStatus === 'PAID' ? 'success' : 'warning'}
                          />
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              </>
            )}

            {/* Notes and Special Requirements */}
            {(appointment.notes || appointment.specialRequirements) && (
              <>
                <Divider sx={{ my: 3 }} />
                <Box>
                  {appointment.notes && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Notes
                      </Typography>
                      <Typography variant="body2">
                        {appointment.notes}
                      </Typography>
                    </Box>
                  )}
                  {appointment.specialRequirements && (
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Special Requirements
                      </Typography>
                      <Typography variant="body2">
                        {appointment.specialRequirements}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
        <Box>
          {isCaregiver && isPending && !showCancelConfirm && (
            <>
              <Button
                variant="contained"
                color="success"
                startIcon={accepting ? <CircularProgress size={16} /> : <CheckCircle />}
                onClick={handleAcceptAppointment}
                disabled={accepting || rejecting}
                sx={{ mr: 1 }}
              >
                {accepting ? 'Accepting...' : 'Accept Request'}
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={rejecting ? <CircularProgress size={16} /> : <Cancel />}
                onClick={handleRejectAppointment}
                disabled={accepting || rejecting}
                sx={{ mr: 1 }}
              >
                {rejecting ? 'Rejecting...' : 'Reject Request'}
              </Button>
            </>
          )}
          {canCancel && !showCancelConfirm && !isPending && (
            <Button
              color="error"
              onClick={() => setShowCancelConfirm(true)}
            >
              Cancel Appointment
            </Button>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={onClose} disabled={accepting || rejecting || cancelling}>
            Close
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default AppointmentDetailsModal;

